import { Box, Flex } from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import React, { Suspense, useMemo } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";

import { SplashScreen } from "../../components/Loader";
import Sidebar from "../../components/Sidebar";

import { LoadScript } from "@react-google-maps/api";
import { GOOGLE_MAP_API_KEY } from "../../constants/app";
import { MenuConstants } from "../../constants/menu";
import { useAuth } from "../../plugins/CookieAuthPlugin";
import ChangePassword from "./change-password";
import Nationality from "./setting/nationality";

const DelayImport = (path) => {
  return React.lazy(() => {
    return Promise.all([path, new Promise((resolve) => setTimeout(resolve, 300))]).then(([moduleExports]) => moduleExports);
  });
};

//Dashboard
const NationalData = DelayImport(import("./dashboard/national"));
const Internationality = DelayImport(import("./dashboard/international"));
const InternationalityOnline = DelayImport(import("./dashboard/international-online"));

//FormData
const TicketSale = DelayImport(import("./form-data/ticket-sale"));
const TouristNationality = DelayImport(import("./form-data/tourist-nationality"));
const TouristTemple = DelayImport(import("./form-data/tourist-temple"));
const TouristAgent = DelayImport(import("./form-data/tourist-tour-agent"));

//Temple
const Temple = DelayImport(import("./temple/temple"));
const TempleGroup = DelayImport(import("./temple/temple-group"));

// CodeConduct
const CodeConduct = DelayImport(import("./code-of-conduct"));

// Others
const Feed = DelayImport(import("./others/feed"));
const Zone = DelayImport(import("./others/zone"));
const Icon = DelayImport(import("./others/icon"));
const Slide = DelayImport(import("./others/slide"));

//MoreThanTemple
const AnnualEvent = DelayImport(import("./more-than-temple/annual-event"));
const Community = DelayImport(import("./more-than-temple/community"));
const Activities = DelayImport(import("./more-than-temple/activities"));
const Museum = DelayImport(import("./more-than-temple/museum"));

//Report
const FeedBack = DelayImport(import("./report/feedback"));
const TicketSaleReport = DelayImport(import("./report/ticket-sale"));
const TouristNationalityReport = DelayImport(import("./report/tourist-nationality"));
const TouristTempleReport = DelayImport(import("./report/tourist-temple"));
const TouristAgentReport = DelayImport(import("./report/tourist-tour-agent"));
const Visitor = DelayImport(import("./report/visitor"));
const Transportation = DelayImport(import("./report/transportation"));
const PurposeVisit = DelayImport(import("./report/purpose-visit"));

//setting
const Contact = DelayImport(import("./setting/contact"));
const Privacy = DelayImport(import("./setting/privacy-term"));
const User = DelayImport(import("./setting/user"));
const SystemRole = DelayImport(import("./setting/system-role"));

//delete user form
const DeleteUserForm = DelayImport(import("../deleteUserRequest"));
const UserDeleteRequst = DelayImport(import("./setting/user-delete-request"));

//Form Scan QR Code
const ScanQrForm = DelayImport(import("../scanQrForm"));

const routeItem = [
  //Dashboard
  { path: "/national-data", component: <NationalData /> },
  { path: "/international-data", component: <Internationality /> },
  { path: "/international-data-online", component: <InternationalityOnline /> },

  //FormData
  { path: "/form-data/ticket-sale", component: <TicketSale /> },
  { path: "/form-data/tourist-by-nationality", component: <TouristNationality /> },
  { path: "/form-data/tourist-at-each-temple", component: <TouristTemple /> },
  { path: "/form-data/tourist-by-tour-agent", component: <TouristAgent /> },

  //Temple
  { path: "/temple", component: <Temple /> },
  { path: "/temple-group", component: <TempleGroup /> },

  // Others
  { path: "/feed", component: <Feed /> },
  { path: "/zone", component: <Zone /> },
  { path: "/icon", component: <Icon /> },
  { path: "/slide", component: <Slide /> },

  //MoreThanTemple
  { path: "/annual-event", component: <AnnualEvent /> },
  { path: "/community", component: <Community /> },
  { path: "/activities", component: <Activities /> },
  { path: "/museum", component: <Museum /> },

  // CodeOfConduct
  { path: "/code-of-conduct", component: <CodeConduct /> },

  //Report
  { path: "/feedback", component: <FeedBack /> },
  { path: "/report/ticket-sale", component: <TicketSaleReport /> },
  { path: "/report/tourist-by-nationality", component: <TouristNationalityReport /> },
  { path: "/report/tourist-each-temple", component: <TouristTempleReport /> },
  { path: "/report/tourist-by-tour-agent", component: <TouristAgentReport /> },
  { path: "/visitor", component: <Visitor /> },
  { path: "/report/transportation", component: <Transportation /> },
  { path: "/report/purpose-of-visit", component: <PurposeVisit /> },

  // Setting Menu
  { path: "/contact", component: <Contact /> },
  { path: "/privacy", component: <Privacy /> },
  { path: "/nationality", component: <Nationality /> },
  { path: "/user-delete-request", component: <UserDeleteRequst /> },
  { path: "/user", component: <User /> },
  { path: "/system-role", component: <SystemRole /> },
  { path: "/change-password", component: <ChangePassword /> },
  { path: "/delete-user-request", component: <DeleteUserForm /> },
  { path: "/form-scan-qrcode", component: <ScanQrForm /> },
];

const MotionBox = motion(Box);
export default function App() {
  const { user } = useAuth();
  const location = useLocation();
  const redirectURL = useMemo(() => {
    if (user.menuPermission) {
      return MenuConstants.reduce((acc, { item }) => [...acc, ...item], []).find(({ id }) => user.menuPermission[id] && user.menuPermission[id]["VIEW"])?.id;
    }
  }, [user.menuPermission]);

  return (
    <LoadScript googleMapsApiKey={`https://maps.google.com/maps/api/js?sensor=false&libraries=drawing&v=3.22&key=${GOOGLE_MAP_API_KEY}`}>
      <Suspense fallback={<SplashScreen />}>
        <Flex position="relative" h="100vh" overflow="auto" id="app-container">
          <Box display="flex" hidden={user?.statusLogin === 1}>
            <Sidebar />
          </Box>
          <Box w="calc(100% - 18rem)" position="relative" flex="1" overflow="auto">
            <Box p="2" h="100vh">
              <AnimatePresence exitBeforeEnter>
                <Switch location={location} key={location.pathname}>
                  {routeItem.map(({ path, component }) => (
                    <Route key={path} path={path}>
                      <MotionBox h="full" overflow="auto" rounded="md" key={location.pathname} initial={{ y: -10, opacity: 0, transition: { duration: 0.15 } }} animate={{ y: 0, opacity: 1, transition: { duration: 0.2 } }} exit={{ y: -20, opacity: 0, transition: { duration: 0.15 } }}>
                        {component}
                      </MotionBox>
                    </Route>
                  ))}
                  {user ? redirectURL === "dashboard" ? <Redirect to="/national-data" /> : <Redirect to={`/${redirectURL}`} /> : user?.statusLogin === 1 && <Redirect to="/change-password" />}
                </Switch>
              </AnimatePresence>
            </Box>
          </Box>
        </Flex>
      </Suspense>
    </LoadScript>
  );
}
